// RUNN — service-worker registration + install-prompt capture.
// Kept tiny and dependency-free; the app/landing just call registerSW().

let deferredPrompt = null;

export function registerSW() {
  if (!('serviceWorker' in navigator)) return;

  // No service worker in dev — proactively clear any stale one so dev never
  // serves cached assets or loops. The SW is active only in production.
  if (import.meta.env.DEV) {
    navigator.serviceWorker.getRegistrations()
      .then((rs) => rs.forEach((r) => r.unregister()))
      .catch(() => {});
    return;
  }

  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { type: 'module' });

      // When a new SW is waiting, surface a soft "update ready" event the UI can use.
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener('statechange', () => {
          if (sw.state === 'installed' && navigator.serviceWorker.controller) {
            window.dispatchEvent(new CustomEvent('runn:update-ready', { detail: reg }));
          }
        });
      });
    } catch (err) {
      console.warn('[RUNN] service worker registration failed', err);
    }
  });
}

/** Apply a waiting update and reload. */
export function applyUpdate(reg) {
  if (reg?.waiting) {
    reg.waiting.postMessage('SKIP_WAITING');
    reg.waiting.addEventListener('statechange', (e) => {
      if (e.target.state === 'activated') window.location.reload();
    });
  }
}

/**
 * Capture the A2HS prompt. Returns a controller the UI can use to know whether
 * install is available and to trigger the native prompt from a click handler.
 */
export function setupInstall(onAvailable) {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    if (typeof onAvailable === 'function') onAvailable(true);
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    if (typeof onAvailable === 'function') onAvailable(false);
  });
}

export function canInstall() {
  return deferredPrompt !== null;
}

export async function promptInstall() {
  if (!deferredPrompt) return { outcome: 'unavailable' };
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice;
}

/** Standalone = launched from the installed icon. */
export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}
